import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { noteText } from './fxFormat';
import FxLockModal from './FxLockModal';
import FxProjectsTable from './FxProjectsTable';
import FxRatesTable from './FxRatesTable';

import { formatDateTime } from 'Helpers/dateTimeService';

import Alert from 'CommonComponents/Alert';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

const SELECT_CLASSES = 'tw-border-2 tw-border-grey tw-rounded-lg tw-text-sm tw-text-charcoal tw-px-3 tw-py-1.5 tw-bg-white';

/** The first validation message of a refused change, or the generic one. */
const refusal = (error) => {
    const errors = error.response?.data?.errors;

    return (errors && Object.values(errors)[0]?.[0]) || error.response?.data?.message || $t('Something went wrong');
};

function AnchorCard({ anchor, currencies, onChange }) {
    return (
        <div className="table__card">
            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
                <div className="tw-max-w-3xl">
                    <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-1">{$t('Anchor currency')}</h2>
                    <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                        {$t(
                            'All rates are entered against this currency. Organisation reports can be shown in the anchor or any other currency in the table. Changing the anchor rebases every rate and lock below; nothing is cleared.',
                        )}
                    </p>
                </div>
                <select
                    className={SELECT_CLASSES}
                    aria-label={$t('Anchor currency')}
                    value={anchor?.id ?? ''}
                    onChange={(event) => onChange(currencies.find((currency) => String(currency.id) === event.target.value))}
                >
                    {!anchor && <option value="">{$t('Choose an anchor currency')}</option>}
                    {currencies.map((currency) => (
                        <option key={currency.id} value={currency.id}>
                            {currency.code}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}

function ChangeLog({ log }) {
    return (
        <div className="table__card">
            <details>
                <summary className="tw-cursor-pointer tw-font-semibold tw-text-blue">{$t('Change log')}</summary>
                <div className="inbud-table_wrapper tw-mt-4">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th className="!tw-text-start">{$t('When')}</th>
                                <th className="!tw-text-start">{$t('Who')}</th>
                                <th className="!tw-text-start">{$t('Change')}</th>
                                <th className="!tw-text-end">{$t('Old value')}</th>
                                <th className="!tw-text-end">{$t('New value')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {log.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="tw-text-center tw-text-charcoal-75">
                                        {$t('No changes yet.')}
                                    </td>
                                </tr>
                            )}
                            {log.map((entry) => (
                                <tr key={entry.id} className="tw-text-sm">
                                    <td className="tw-text-start tw-whitespace-nowrap">{entry.created_at ? formatDateTime(new Date(entry.created_at)) : ''}</td>
                                    <td className="tw-text-start">{entry.user || $t('System')}</td>
                                    <td className="tw-text-start">{describe(entry)}</td>
                                    <td className="inbud-numeric-cell">{logValue(entry, entry.old_value)}</td>
                                    <td className="inbud-numeric-cell">{logValue(entry, entry.new_value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </div>
    );
}

/** A logged value: a rate as stored, trailing zeros trimmed; an anchor change as its currency code. */
function logValue(entry, value) {
    if (value === null || value === undefined) {
        return '—';
    }

    if (entry.action === 'anchor_set' || entry.action === 'anchor_changed') {
        return value;
    }

    return String(value).includes('.') ? String(value).replace(/0+$/, '').replace(/\.$/, '') : String(value);
}

/** One change log row as a sentence. */
function describe(entry) {
    const values = { currency: entry.currency || '—', project: entry.project || '—' };
    const sentences = {
        anchor_set: () => $t('Anchor currency set to {{currency}}', values),
        anchor_changed: () => $t('Anchor currency changed; every rate and lock rebased'),
        rate_added: () => $t('{{currency}} added to the rate table', values),
        rate_auto_added: () => $t('{{currency}} added automatically', values),
        rate_changed: () => $t('{{currency}} common rate changed', values),
        rate_rebased: () => $t('{{currency}} common rate rebased', values),
        rate_removed: () => $t('{{currency}} removed from the rate table', values),
        rate_reviewed: () => $t('{{currency}} common rate marked reviewed', values),
        locked: () => $t('{{project}} locked', values),
        lock_changed: () => $t('{{project}} locked rate changed', values),
        lock_rebased: () => $t('{{project}} locked rate rebased', values),
        unlocked: () => $t('{{project}} unlocked, now follows the common rate', values),
        lock_removed: () => $t('{{project}} lock removed', values),
        lock_cleared: () =>
            $t('{{project}} lock cleared: currency changed from {{old}} to {{new}}', {
                ...values,
                old: entry.note_params?.old || '—',
                new: entry.note_params?.new || '—',
            }),
        lock_reviewed: () => $t('{{project}} marked reviewed', values),
    };

    const sentence = sentences[entry.action] ? sentences[entry.action]() : entry.action;

    // A cleared lock's sentence already says why.
    const note = entry.action === 'lock_cleared' ? null : noteText(entry.note_key, entry.note_params);

    return note ? `${sentence}: ${note}` : sentence;
}

/**
 * The organisation's FX page: the anchor currency, the common rates, every
 * project's rate and the change log. Every change answers with the whole page.
 */
export default function OrganisationFxIndex(props) {
    const [page, setPage] = useState(null);
    const [error, setError] = useState(null);
    const [lockProject, setLockProject] = useState(null);

    const toast = useToast();
    const confirm = useConfirmationModal();

    useEffect(() => {
        axios
            .get(props.dataUrl)
            .then(({ data }) => setPage(data))
            .catch(() => setError($t('Something went wrong')));
    }, [props.dataUrl]);

    // Every write answers with the whole page; a refusal says why.
    const send = useCallback(
        (request, success = null) =>
            request
                .then(({ data }) => {
                    setPage(data);
                    if (success) {
                        toast.success(success);
                    }

                    return true;
                })
                .catch((e) => {
                    toast.error(refusal(e));

                    return false;
                }),
        [toast],
    );

    if (!page) {
        return error ? <Alert type="error">{error}</Alert> : <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>;
    }

    const anchor = page.anchor;
    const rateUrl = (template, row) => template.replace('__RATE__', row.id);
    const eventUrl = (template, project) => template.replace('__EVENT__', project.slug);

    const changeAnchor = (currency) => {
        if (!currency || currency.id === anchor?.id) {
            return;
        }

        if (!anchor) {
            send(axios.put(props.anchorUrl, { currency_id: currency.id }), $t('Anchor currency set to {{currency}}', { currency: currency.code }));

            return;
        }

        confirm({
            title: $t('Change anchor currency?'),
            message: $t(
                'Every common rate and project lock will be recalculated against the new anchor and flagged for review. Nothing is cleared and organisation reports keep working.',
            ),
            actions: [
                { confirmed: false, label: $t('Keep {{currency}}', { currency: anchor.code }), variant: 'secondary', onClick: () => {} },
                {
                    confirmed: true,
                    label: $t('Change and rebase rates'),
                    variant: 'primary',
                    onClick: () =>
                        send(
                            axios.put(props.anchorUrl, { currency_id: currency.id }),
                            $t('Anchor changed to {{currency}}. Rates rebased, please review', { currency: currency.code }),
                        ),
                },
            ],
        });
    };

    const unlock = (project) =>
        send(
            axios.delete(eventUrl(props.lockUrl, project)),
            $t('{{project}} follows the {{currency}} common rate', { project: project.name, currency: project.currency }),
        );

    return (
        <div className="tw-space-y-4">
            <div>
                <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                    {$t(
                        "Set the organisation's reporting currency and the rate used to show each project in it. Rates are used for organisation reports only. Nothing inside a project changes.",
                    )}
                </p>
            </div>

            <AnchorCard anchor={anchor} currencies={page.currencies} onChange={changeAnchor} />

            {!anchor ? (
                <Alert type="info" role="status">
                    {$t('Choose an anchor currency to start entering rates.')}
                </Alert>
            ) : (
                <>
                    <FxRatesTable
                        rates={page.rates}
                        currencies={page.currencies}
                        anchor={anchor.code}
                        onSave={(row, payload) =>
                            send(axios.put(rateUrl(props.rateUpdateUrl, row), payload), $t('{{currency}} rate saved', { currency: row.currency }))
                        }
                        onAdd={(payload) => send(axios.post(props.ratesStoreUrl, payload), $t('Currency added'))}
                        onRemove={(row) => send(axios.delete(rateUrl(props.rateDeleteUrl, row)), $t('Currency removed'))}
                        onReview={(row) => send(axios.post(rateUrl(props.rateReviewUrl, row)), $t('Marked reviewed'))}
                    />

                    <FxProjectsTable
                        projects={page.projects}
                        onLock={setLockProject}
                        onUnlock={unlock}
                        onReview={(project) => send(axios.post(eventUrl(props.lockReviewUrl, project)), $t('Marked reviewed'))}
                    />
                </>
            )}

            <ChangeLog log={page.log} />

            {lockProject && (
                <FxLockModal
                    project={lockProject}
                    anchor={anchor.code}
                    lockUrl={eventUrl(props.lockUrl, lockProject)}
                    onSaved={setPage}
                    onClose={() => setLockProject(null)}
                />
            )}
        </div>
    );
}

if (document.getElementById('OrganisationFx')) {
    const rootEl = document.getElementById('OrganisationFx');

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <OrganisationFxIndex
                    dataUrl={rootEl.getAttribute('data-data-url')}
                    anchorUrl={rootEl.getAttribute('data-anchor-url')}
                    ratesStoreUrl={rootEl.getAttribute('data-rates-store-url')}
                    rateUpdateUrl={rootEl.getAttribute('data-rate-update-url')}
                    rateDeleteUrl={rootEl.getAttribute('data-rate-delete-url')}
                    rateReviewUrl={rootEl.getAttribute('data-rate-review-url')}
                    lockUrl={rootEl.getAttribute('data-lock-url')}
                    lockReviewUrl={rootEl.getAttribute('data-lock-review-url')}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
